import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { UserBiography } from '@/hooks/useBiographyLibrary';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

interface BiographyEditorModalProps {
  biography: UserBiography | null;
  isOpen: boolean;
  onClose: () => void;
  createBiography: any;
  updateBiography: any;
  isLoading: boolean;
}

export const BiographyEditorModal: React.FC<BiographyEditorModalProps> = ({
  biography,
  isOpen,
  onClose,
  createBiography,
  updateBiography,
  isLoading: libraryLoading,
}) => {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    biography_name: '',
    first_name: '',
    preferred_name: '',
    name_usage_preference: 'first' as 'first' | 'preferred' | 'random',
    biography_text: '',
    is_default: false,
  });

  const [validationErrors, setValidationErrors] = useState({
    biography_name: false,
    biography_text: false,
  });

  useEffect(() => {
    if (isOpen) {
      setSaveError(null);
      if (biography) {
        setFormData({
          biography_name: biography.biography_name,
          first_name: biography.first_name || '',
          preferred_name: biography.preferred_name || '',
          name_usage_preference: biography.name_usage_preference,
          biography_text: biography.biography_text,
          is_default: biography.is_default,
        });
      } else {
        setFormData({
          biography_name: '',
          first_name: '',
          preferred_name: '',
          name_usage_preference: 'first',
          biography_text: '',
          is_default: false,
        });
        // Auto-focus the name input for new biographies
        setTimeout(() => {
          nameInputRef.current?.focus();
        }, 100);
      }
      setValidationErrors({ biography_name: false, biography_text: false });
    }
  }, [biography, isOpen]);

  const handleSubmit = async () => {
    // Check character limit first
    if (isOverLimit) {
      toast.error(
        `Biography is too long. Please shorten it to ${maxChars} characters or fewer.`
      );
      return;
    }

    // Validate required fields
    const errors = {
      biography_name: !formData.biography_name.trim(),
      biography_text: !formData.biography_text.trim(),
    };

    setValidationErrors(errors);

    if (errors.biography_name || errors.biography_text) {
      toast.error('Please fill in Biography Name and Biography Text');
      return;
    }

    try {
      setSaveError(null);
      if (biography) {
        await updateBiography.mutateAsync({
          id: biography.id,
          updates: formData,
        });
      } else {
        await createBiography.mutateAsync(formData);
      }
      onClose();
    } catch (error: any) {
      console.error('Failed to save biography:', error);
      const errorMessage =
        error?.message || 'Failed to save biography. Please try again.';
      setSaveError(errorMessage);
    }
  };

  const characterCount = formData.biography_text.length;
  const maxChars = 3000;

  const isFormInvalid =
    !formData.biography_name.trim() || !formData.biography_text.trim();
  const isOverLimit = characterCount > maxChars;
  const isSaveDisabled =
    isFormInvalid ||
    createBiography.isPending ||
    updateBiography.isPending ||
    libraryLoading;

  let disabledReason = '';
  if (isFormInvalid) {
    disabledReason = 'Fill in all required fields (*) to save';
  } else if (
    createBiography.isPending ||
    updateBiography.isPending ||
    libraryLoading
  ) {
    disabledReason = 'Saving...';
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {biography ? 'Edit Biography' : 'Add New Biography'}
          </DialogTitle>
        </DialogHeader>

        {/* Error Banners */}
        {saveError && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">
                Could not save biography
              </p>
              <p className="text-sm text-destructive/90 mt-1">{saveError}</p>
            </div>
          </div>
        )}

        {isOverLimit && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">
                Biography is too long
              </p>
              <p className="text-sm text-destructive/90 mt-1">
                Please shorten your biography to {maxChars} characters or fewer
                before saving.
              </p>
            </div>
          </div>
        )}

        {libraryLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        <div className="space-y-4 py-4">
          {/* Biography Name */}
          <div>
            <Label htmlFor="biography_name" className="text-sm font-medium">
              Biography Name <span className="text-destructive text-base">*</span>
            </Label>
            <Input
              ref={nameInputRef}
              id="biography_name"
              placeholder="e.g., Mom, Best Friend Sarah, My Sister"
              value={formData.biography_name}
              onChange={(e) => {
                setFormData({ ...formData, biography_name: e.target.value });
                setValidationErrors({
                  ...validationErrors,
                  biography_name: false,
                });
              }}
              className={
                validationErrors.biography_name
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }
            />
            {validationErrors.biography_name && (
              <p className="text-xs text-destructive mt-1">
                This field is required
              </p>
            )}
            {!validationErrors.biography_name && (
              <p className="text-xs text-muted-foreground mt-1">
                A name to identify this biography in your library
              </p>
            )}
          </div>

          {/* First Name */}
          <div>
            <Label htmlFor="first_name">First Name</Label>
            <Input
              id="first_name"
              placeholder="e.g., Sarah"
              value={formData.first_name}
              onChange={(e) =>
                setFormData({ ...formData, first_name: e.target.value })
              }
            />
          </div>

          {/* Preferred Name */}
          <div>
            <Label htmlFor="preferred_name">Preferred Name</Label>
            <Input
              id="preferred_name"
              placeholder="e.g., Nickname"
              value={formData.preferred_name}
              onChange={(e) =>
                setFormData({ ...formData, preferred_name: e.target.value })
              }
            />
          </div>

          {/* Name Usage Preference */}
          <div>
            <Label htmlFor="name_usage">Name Usage in Sessions</Label>
            <Select
              value={formData.name_usage_preference}
              onValueChange={(value: 'first' | 'preferred' | 'random') =>
                setFormData({ ...formData, name_usage_preference: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="first">Use First Name</SelectItem>
                <SelectItem value="preferred">Use Preferred Name</SelectItem>
                <SelectItem value="random">Random Mix</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Default Biography Toggle */}
          <div className="flex items-start justify-between gap-4 py-2">
            <div className="flex-1">
              <Label htmlFor="is_default" className="text-base">
                Set as my default biography
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                This biography will be used by default when creating sessions
              </p>
            </div>
            <Switch
              id="is_default"
              checked={formData.is_default}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_default: checked })
              }
            />
          </div>

          {/* Biography Text */}
          <div>
            <Label htmlFor="biography_text" className="text-sm font-medium">
              Biography <span className="text-destructive text-base">*</span>
            </Label>
            <Textarea
              id="biography_text"
              placeholder="Write about this person... their personality, interests, challenges, goals, etc."
              value={formData.biography_text}
              onChange={(e) => {
                setFormData({ ...formData, biography_text: e.target.value });
                setValidationErrors({
                  ...validationErrors,
                  biography_text: false,
                });
              }}
              rows={10}
              className={`resize-none ${
                validationErrors.biography_text
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }`}
            />
            {validationErrors.biography_text && (
              <p className="text-xs text-destructive mt-1">
                This field is required
              </p>
            )}
            {isOverLimit && (
              <p className="text-xs text-destructive font-semibold mt-1">
                Biography is too long — shorten it to {maxChars} characters or
                fewer.
              </p>
            )}
            <div className="flex justify-between items-center mt-1">
              {!validationErrors.biography_text && !isOverLimit && (
                <div className="flex flex-col gap-0.5">
                  <p className="text-xs text-muted-foreground">
                    Aim for 1-3 short paragraphs. Max {maxChars} characters.
                  </p>
                  <p className="text-xs text-muted-foreground/70">
                    🌟 For teams: include up to 15 people with roles - each will be honored by name.
                  </p>
                </div>
              )}
              <p
                className={`text-xs ml-auto font-semibold ${
                  characterCount > maxChars
                    ? 'text-destructive'
                    : 'text-muted-foreground'
                }`}
              >
                {characterCount} / {maxChars}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex flex-col items-stretch sm:items-end gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      onClick={handleSubmit}
                      disabled={isSaveDisabled}
                      className="w-full sm:w-auto"
                    >
                      {createBiography.isPending || updateBiography.isPending
                        ? 'Saving...'
                        : isOverLimit
                        ? `${
                            biography ? 'Update' : 'Add'
                          } Biography (shorten text to save)`
                        : biography
                        ? 'Update Biography'
                        : 'Add Biography'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {disabledReason && (
                  <TooltipContent>
                    <p className="text-sm">{disabledReason}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            {disabledReason && (
              <p className="text-xs text-muted-foreground text-right">
                {disabledReason}
              </p>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BiographyEditorModal;
